import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { VISIT_RISK_STATUSES, type VisitRiskStatus } from '@/clinical-study/vocabulary';
import type { paths } from '@/lib/api-types-clinical-study';
import { formatDate } from '@/lib/intl';

export type AtRiskVisitEntry =
  paths['/v1/clinical-study/studies/{studyId}/at-risk-visits']['get']['responses'][200]['content']['application/json'][number];

// Resolution R7: deliberately duplicated from `ScheduleSection.tsx`'s own
// `TIMEPOINT_TYPE_LABEL_KEY`/`VISIT_STATE_LABEL_KEY` local `as const` maps — both reuse the SAME
// existing `schedule.*` i18n keys (no new keys added), keeping `ScheduleSection.tsx` untouched.
const TIMEPOINT_TYPE_LABEL_KEY = {
  TREATMENT_ANCHOR: 'schedule.typeAnchor',
  FOLLOW_UP: 'schedule.typeFollowUp',
} as const;
const VISIT_STATE_LABEL_KEY = {
  PLANNED: 'schedule.statePLANNED',
  SCHEDULED: 'schedule.stateSCHEDULED',
  COMPLETED: 'schedule.stateCOMPLETED',
  MISSED: 'schedule.stateMISSED',
  CANCELLED: 'schedule.stateCANCELLED',
} as const;
const RISK_LABEL_KEY = {
  ELAPSING: 'atRisk.riskELAPSING',
  ELAPSED: 'atRisk.riskELAPSED',
} as const;
const RISK_BADGE_TONE = {
  ELAPSING: 'warning',
  ELAPSED: 'error',
} as const;

/**
 * Shared presentational at-risk table (Resolution R2/R6) — reused by `AtRiskVisitsSection.tsx`
 * (operational, `appointment:manage`) and `oversight/StudyOversightPage.tsx` (CRO monitor,
 * `study:monitor`); both endpoints return the byte-identical `AtRiskVisitResponseDto[]` shape.
 * Preserves the server's own `windowEnd`-ascending order (never re-sorts, only filters). The
 * `onDrillDown` prop is optional: when provided, an actions column renders a per-row "open Study
 * Subject" control (operational use only — the CRO monitor has no subject workspace to drill
 * into, so `StudyOversightPage.tsx` omits this prop and the column does not render).
 */
export function AtRiskVisitsTable({
  entries,
  onDrillDown,
}: {
  entries: AtRiskVisitEntry[];
  onDrillDown?: (subjectId: string) => void;
}) {
  const { t } = useTranslation('clinical-study');
  const [riskFilter, setRiskFilter] = useState<'all' | VisitRiskStatus>('all');

  const filteredEntries =
    riskFilter === 'all' ? entries : entries.filter((entry) => entry.riskStatus === riskFilter);

  const baseColumns: ColumnDef<AtRiskVisitEntry, unknown>[] = [
    {
      id: 'subject',
      header: t('atRisk.subjectColumnLabel'),
      cell: ({ row }) => row.original.definitiveIdentifier ?? row.original.provisionalIdentifier,
    },
    {
      id: 'timepoint',
      header: t('atRisk.timepointColumnLabel'),
      cell: ({ row }) => t(TIMEPOINT_TYPE_LABEL_KEY[row.original.timepointType]),
    },
    {
      accessorKey: 'targetDate',
      header: t('atRisk.targetDateColumnLabel'),
      cell: ({ getValue }) => formatDate(getValue<string>()),
    },
    {
      id: 'window',
      header: t('atRisk.windowColumnLabel'),
      cell: ({ row }) =>
        `${formatDate(row.original.windowStart)} – ${formatDate(row.original.windowEnd)}`,
    },
    {
      id: 'visitState',
      header: t('atRisk.visitStateColumnLabel'),
      cell: ({ row }) => t(VISIT_STATE_LABEL_KEY[row.original.lifecycleState]),
    },
    {
      id: 'risk',
      header: t('atRisk.riskColumnLabel'),
      cell: ({ row }) => (
        <span className={`status-badge ${RISK_BADGE_TONE[row.original.riskStatus]}`}>
          {t(RISK_LABEL_KEY[row.original.riskStatus])}
        </span>
      ),
    },
  ];

  const columns: ColumnDef<AtRiskVisitEntry, unknown>[] = onDrillDown
    ? [
        ...baseColumns,
        {
          id: 'actions',
          header: '',
          cell: ({ row }) => (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label={t('atRisk.drillDownActionForRowLabel', {
                identifier: row.original.definitiveIdentifier ?? row.original.provisionalIdentifier,
              })}
              onClick={() => onDrillDown(row.original.studySubjectId)}
            >
              {t('atRisk.drillDownAction')}
            </Button>
          ),
        },
      ]
    : baseColumns;

  return (
    <div className="space-y-3">
      <div
        role="group"
        aria-label={t('atRisk.riskFilterGroupLabel')}
        className="border-border bg-background inline-flex items-center gap-0.5 rounded-full
          border p-0.5"
      >
        {(['all', ...VISIT_RISK_STATUSES] as const).map((option) => {
          const active = riskFilter === option;
          const label = option === 'all' ? t('atRisk.filterAll') : t(RISK_LABEL_KEY[option]);
          return (
            <button
              key={option}
              type="button"
              aria-pressed={active}
              onClick={() => setRiskFilter(option)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <DataTable<AtRiskVisitEntry, unknown>
        columns={columns}
        data={filteredEntries}
        emptyMessage={t('atRisk.emptyMessage')}
      />
    </div>
  );
}
